import React, { useEffect } from 'react';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ClearIcon from '@material-ui/icons/Clear';
// @ts-ignore
import { useRowSelect, useSortBy, useTable } from 'react-table';
import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { EeCheckbox } from '../EeCheckbox/EeCheckbox';

const toString = (a: number | string | undefined) => {
  if (typeof a === 'number') {
    if (isNaN(a) || a === Infinity || a === -Infinity) {
      return '';
    }
    return String(a);
  }
  if (typeof a === 'string') {
    return a;
  }
  return '';
};

export interface EeTableProps<T> {
  columns: {
    Header: string;
    // FixMe: I doubt ((originalRow: any) => Date) is correct. See CompanyStatusPage -> columns
    accessor: string | ((originalRow: any) => Date);
  }[];
  selectable?: boolean;
  data: T[];
  defaultSortBy?: { id: string; desc: boolean };
  onSelect?: (selectedRows: T[]) => void;
  onRowEdit?: ({ row }: { row: any }) => void;
  onRowDelete?: ({ row }: { row: any }) => void;
}

export const EeTable: React.FC<EeTableProps<{}>> = ({
  columns,
  selectable,
  data,
  defaultSortBy,
  onSelect,
  onRowEdit,
  onRowDelete,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { selectedRowIds },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          defaultSortBy || {
            id: columns[0].accessor,
            desc: false,
          },
        ],
      },
      sortTypes: {
        ialphanumeric: (rowA: any, rowB: any, columnId: string) => {
          const reSplitAlphaNumeric = /([0-9]+)/gm;
          let a = rowA.values[columnId];
          let b = rowB.values[columnId];
          // Force to strings (or "" for unsupported types)
          a = toString(a).toLowerCase();
          b = toString(b).toLowerCase();

          // Split on number groups, but keep the delimiter
          // Then remove falsey split values
          a = a.split(reSplitAlphaNumeric).filter(Boolean);
          b = b.split(reSplitAlphaNumeric).filter(Boolean);

          // While
          while (a.length && b.length) {
            let aa = a.shift();
            let bb = b.shift();

            const an = parseInt(aa, 10);
            const bn = parseInt(bb, 10);

            const combo = [an, bn].sort();

            // Both are string
            if (isNaN(combo[0])) {
              if (aa > bb) {
                return 1;
              }
              if (bb > aa) {
                return -1;
              }
              continue;
            }

            // One is a string, one is a number
            if (isNaN(combo[1])) {
              return isNaN(an) ? -1 : 1;
            }

            // Both are numbers
            if (an > bn) {
              return 1;
            }
            if (bn > an) {
              return -1;
            }
          }

          return a.length - b.length;
        },
      },
    },
    useSortBy,
    useRowSelect,
    (hooks: any) => {
      hooks.visibleColumns.push((columns: any) =>
        selectable
          ? [
              // Let's make a column for selection
              {
                id: 'selection',
                // The header can use the table's getToggleAllRowsSelectedProps method
                // to render a checkbox
                Header: ({
                  getToggleAllRowsSelectedProps,
                }: {
                  getToggleAllRowsSelectedProps: any;
                }) => (
                  <div>
                    <EeCheckbox {...getToggleAllRowsSelectedProps()} />
                  </div>
                ),
                // The cell can use the individual row's getToggleRowSelectedProps method
                // to the render a checkbox
                Cell: ({ row }: { row: any }) => (
                  <div>
                    <EeCheckbox {...row.getToggleRowSelectedProps()} />
                  </div>
                ),
              },
              ...columns,
            ]
          : columns,
      );
    },
  );

  useEffect(() => {
    onSelect && onSelect(selectedFlatRows.map((row: any) => row.original));
  }, [selectedRowIds]);

  return (
    <Table {...getTableProps()} className="ee-table lens-table">
      <TableHead className="ee-table-thead">
        {headerGroups.map((headerGroup: any) => (
          <TableRow {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column: any) => (
              <TableCell
                variant="head"
                width={column.id === 'selection' ? '1%' : ''}
                {...(column.id === 'selection'
                  ? column.getHeaderProps()
                  : column.getHeaderProps(column.getSortByToggleProps()))}
              >
                {column.render('Header')}
                {column.id !== 'selection' ? (
                  <TableSortLabel
                    active={column.isSorted}
                    // react-table has a unsorted state which is not treated here
                    direction={column.isSortedDesc ? 'desc' : 'asc'}
                  />
                ) : null}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHead>
      <TableBody {...getTableBodyProps({ className: 'ee-table-thead' })}>
        {rows.map((row: any) => {
          prepareRow(row);
          return (
            <TableRow {...row.getRowProps({ className: 'tr-hoverable' })}>
              {row.cells.map((cell: any, index: number, array: any[]) => {
                return (
                  <TableCell
                    variant="body"
                    {...cell.getCellProps(
                      onRowEdit || onRowDelete
                        ? { className: 'position-relative' }
                        : {},
                    )}
                  >
                    {index === array.length - 1 && (
                      <div className="tr-actions">
                        {onRowEdit && (
                          <IconButton onClick={() => onRowEdit({ row })}>
                            <EditOutlinedIcon />
                          </IconButton>
                        )}
                        {onRowDelete && (
                          <IconButton onClick={() => onRowDelete({ row })}>
                            <ClearIcon />
                          </IconButton>
                        )}
                      </div>
                    )}
                    {cell.render('Cell')}
                  </TableCell>
                );
              })}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
